import type { TutorialSlide } from '../screens/TutorialScreen'
import { NeonColors, FONTS } from '../neon-theme'
import { HEADING_FONT, TITLE_FONT_SIZE } from './tutorialStyles'
import { generateSnakeColors, getPlayerColorKey } from '../../game/snakeColors'

const SCALE = 2
const BASE_SPEED = 1000
const TURN_RATE = 4.2
const SEGMENT_SPACING = 60
const INITIAL_WIDTH = 120
const WORLD_SCALE = 0.1
const AVOIDANCE_DIST = 700
const LOOP_DURATION = 46

// Scene 1 (0–9): 2.5s per text, things appear with each text
//   0–2.5:   "Always ≈ 30 snakes" (empty field, just grid)
//   2.5–5:   "No players? Bots fill in" (bots appear)
//   5–7.5:   "Players join — bots evacuate" (players spawn, bots flee)
//   7.5–9:   fade out
// Scene 2 (10–19): collector + hunter
// Scene 3 (19–25): 8 hunters chase big player
// Scene 4 (25–28): "Same rules / not OP"
// Scene 5 (28–31): "Every bot pays 100 sparks"
// Fade (31–33/33–35)

interface Seg { x: number; z: number }
interface DeathSpark { x: number; z: number; vx: number; vz: number; alpha: number }
interface SparkDot { x: number; z: number; collected: boolean }

function normalizeRadians(a: number): number {
  let r = a % (Math.PI * 2)
  if (r > Math.PI) r -= Math.PI * 2
  if (r < -Math.PI) r += Math.PI * 2
  return r
}

class Snake {
  headX: number; headZ: number; bearing: number; desiredBearing: number
  segments: Seg[] = []; alive = true; visible = true
  constructor(x: number, z: number, bearing: number, segCount: number) {
    this.headX = x; this.headZ = z; this.bearing = bearing; this.desiredBearing = bearing
    for (let i = 0; i < segCount; i++) {
      this.segments.push({ x: x - Math.cos(bearing) * i * SEGMENT_SPACING, z: z - Math.sin(bearing) * i * SEGMENT_SPACING })
    }
  }
  step(dt: number, speed = BASE_SPEED): void {
    if (!this.alive) return
    const maxD = TURN_RATE * dt
    const diff = normalizeRadians(this.desiredBearing - this.bearing)
    this.bearing = Math.abs(diff) <= maxD ? normalizeRadians(this.desiredBearing) : normalizeRadians(this.bearing + Math.sign(diff) * maxD)
    this.headX += Math.cos(this.bearing) * speed * dt; this.headZ += Math.sin(this.bearing) * speed * dt
    this.segments[0] = { x: this.headX, z: this.headZ }
    for (let i = 1; i < this.segments.length; i++) {
      const prev = this.segments[i - 1]!; const cur = this.segments[i]!
      const dx = cur.x - prev.x, dz = cur.z - prev.z
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist > SEGMENT_SPACING) { const r = SEGMENT_SPACING / dist; cur.x = prev.x + dx * r; cur.z = prev.z + dz * r }
    }
  }
  grow(): void { const t = this.segments[this.segments.length - 1]!; this.segments.push({ x: t.x, z: t.z }) }
}

export class BotsSlide implements TutorialSlide {
  private container: HTMLDivElement | null = null
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private canvasW = 0; private canvasH = 0
  private animFrame = 0; private startTime = 0
  private lastFrameTime = 0; private mounted = false

  // Scene 1
  private s1Bots: Snake[] = []
  private s1Players: Snake[] = []
  private s1PlayerColors: Array<{ b1: string; b2: string; h: string; g: string }> = []
  private s1Sparks: SparkDot[] = []
  private s1BotTargets: Array<{ x: number; z: number }> = []
  private s1BotStartTimes: number[] = []
  // Scene 2
  private collector!: Snake; private hunter!: Snake
  private collSparks: SparkDot[] = []
  private collDead = false; private collDeathSparks: DeathSpark[] = []
  private bot1HunterMode = false
  private allSparksEatenTime = 0
  private hunterTextTime = 0
  private killTime = 0 // when victim was killed
  // Scene 3
  private s3Player!: Snake; private s3Hunters: Snake[] = []
  // Scene 4
  private s4Player!: Snake; private s4Bot!: Snake
  private s4Dead = false; private s4Sparks: DeathSpark[] = []

  private pB1 = ''; private pB2 = ''; private pH = ''; private pG = ''
  private camX = 0; private camZ = 0

  mount(el: HTMLDivElement, _onReady: () => void): void {
    this.container = el; el.style.padding = '0'
    const s = localStorage.getItem('star-snake-color-seed')
    const cs = s !== null ? parseInt(s, 10) || 0 : 0
    const c = generateSnakeColors(getPlayerColorKey(cs))
    const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`
    this.pB1 = hex(c.body[0]); this.pB2 = hex(c.body[1]); this.pH = hex(c.head); this.pG = hex(c.body[0])
    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText = 'position: absolute; inset: 0; width: 100%; height: 100%;'
    el.appendChild(this.canvas)
    this.mounted = true; this.canvasW = 0; this.canvasH = 0; this.animFrame = 0
    this.startTime = performance.now(); this.lastFrameTime = this.startTime
    this.resetAll(); this.tick()
  }

  unmount(): void {
    this.mounted = false
    if (this.animFrame) { cancelAnimationFrame(this.animFrame); this.animFrame = 0 }
    if (this.container) this.container.innerHTML = ''; this.container = null; this.canvas = null; this.ctx = null
  }

  private resetAll(): void {
    this.camX = 0; this.camZ = 0

    // Scene 1: 8 bots from all corners, mixed white/dark
    // 1 top, 2 upper-left, 2 lower-left, 2 upper-right, 1 lower-right
    this.s1Bots = [
      new Snake(200, -4000, Math.PI * 0.5, 9),       // top-center (white)
      new Snake(-3500, -2000, 0.5, 8),                // upper-left (dark)
      new Snake(-3800, -800, 0.3, 10),                // upper-left-2 (white)
      new Snake(-3500, 1500, -0.15, 9),               // lower-left (white)
      new Snake(-3000, 2500, -0.4, 8),                // lower-left-2 (dark)
      new Snake(3500, -1800, Math.PI - 0.4, 10),      // upper-right (white)
      new Snake(3800, -500, Math.PI + 0.2, 9),        // upper-right-2 (dark)
      new Snake(3500, 2000, Math.PI + 0.5, 8),        // lower-right (white)
    ]
    for (const b of this.s1Bots) b.visible = false
    // Staggered start times so bots don't all arrive at once
    this.s1BotStartTimes = [0.5, 0.8, 1.1, 0.6, 1.3, 0.9, 1.5, 1.0]
    // Each bot targets a point well spread across the visible area
    // Screen visible ≈ ±1600wu, so targets spread across ±1000wu
    this.s1BotTargets = [
      { x: 100, z: -800 },    // top-center bot → stays upper
      { x: -900, z: -500 },   // upper-left → far left-upper
      { x: -500, z: 200 },    // upper-left-2 → center-left
      { x: -800, z: 800 },    // lower-left → far left-lower
      { x: -100, z: 900 },    // lower-left-2 → lower center
      { x: 800, z: -600 },    // upper-right → far right-upper
      { x: 500, z: 100 },     // upper-right-2 → center-right
      { x: 900, z: 700 },     // lower-right → far right-lower
    ]
    // 4 players: 2 from bottom, 2 from top — all with different game colors
    const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`
    const playerSeeds = [0, 15, 32, 48] // different color seeds
    this.s1Players = [
      new Snake(-1200, 3800, -Math.PI / 3, 14),   // bottom-left, heading up-right
      new Snake(1400, 4000, -Math.PI * 0.6, 12),   // bottom-right, heading up-left
      new Snake(-800, -3800, Math.PI / 2 + 0.2, 13), // top-left, heading down-right
      new Snake(1000, -4000, Math.PI / 2 - 0.15, 11), // top-right, heading down-left
    ]
    for (const p of this.s1Players) p.visible = false
    this.s1PlayerColors = playerSeeds.map(seed => {
      const c = generateSnakeColors(getPlayerColorKey(seed))
      return { b1: hex(c.body[0]), b2: hex(c.body[1]), h: hex(c.head), g: hex(c.body[0]) }
    })
    this.s1Sparks = []

    // Scene 2: one bot does everything — collects down, turns up as hunter, kills victim
    this.collector = new Snake(0, -600, Math.PI / 2, 10) // starts near top, heading down
    this.collector.visible = false
    this.collSparks = [
      { x: 30, z: -300, collected: false }, { x: -50, z: -50, collected: false },
      { x: 40, z: 200, collected: false }, { x: -30, z: 450, collected: false },
      { x: 20, z: 700, collected: false }, { x: -40, z: 950, collected: false },
      { x: 10, z: 1200, collected: false },
    ]
    // Victim: fat white collector enters from TOP, heading down
    this.hunter = new Snake(100, -3500, Math.PI / 2 + 0.1, 22); this.hunter.visible = false
    this.collDead = false; this.collDeathSparks = []; this.bot1HunterMode = false; this.allSparksEatenTime = 0; this.hunterTextTime = 0; this.killTime = 0

    // Scene 3: big player + 8 hunters widely spread, from left and top-left
    this.s3Player = new Snake(-4000, 0, 0, 30)
    this.s3Hunters = [
      // 3 trailing behind (far left, widely spread vertically)
      new Snake(-5500, -1200, 0.2, 10),
      new Snake(-6500, 200, 0.1, 10),
      new Snake(-5000, 1500, 0.15, 10),
      // 3 from top-left (angled down-right)
      new Snake(-3000, -4000, Math.PI * 0.4, 10),
      new Snake(-1500, -4500, Math.PI * 0.45, 10),
      new Snake(-4000, -3500, Math.PI * 0.35, 10),
      // 2 from top (heading down)
      new Snake(500, -4000, Math.PI * 0.55, 10),
      new Snake(-500, -4500, Math.PI * 0.5, 10),
    ]

    // Scene 4: player from RIGHT heading left, bot from LEFT heading right, head-on
    // Player does sharp upward turn before collision → bot crashes into player's body
    this.s4Player = new Snake(3000, 200, Math.PI, 18) // from right, heading left
    this.s4Bot = new Snake(-3000, 0, 0, 12) // from left, heading right
    this.s4Dead = false; this.s4Sparks = []
  }

  private resizeCanvas(): void {
    if (!this.canvas) return
    const rect = this.canvas.getBoundingClientRect()
    const w = Math.floor(rect.width) || 320; const h = Math.floor(rect.height) || 500
    if (w === this.canvasW && h === this.canvasH) return
    this.canvasW = w; this.canvasH = h; this.canvas.width = w * SCALE; this.canvas.height = h * SCALE
    this.ctx = this.canvas.getContext('2d')
  }

  private tick = (): void => {
    if (!this.mounted || !this.canvas) return
    this.resizeCanvas()
    if (!this.ctx || this.canvasW === 0) { this.animFrame = requestAnimationFrame(this.tick); return }
    const now = performance.now(); const elapsed = (now - this.startTime) / 1000
    const t = elapsed % LOOP_DURATION; const dt = Math.min(0.1, (now - this.lastFrameTime) / 1000)
    this.lastFrameTime = now
    if (t >= 44 || (t < 0.05 && elapsed > 0.5)) this.resetAll()
    this.simulate(t, dt); this.draw(t); this.animFrame = requestAnimationFrame(this.tick)
  }

  private ss(s: Snake, dt: number, speed = BASE_SPEED): void {
    if (!s.alive || !s.visible) return
    let r = dt; while (r > 0) { const step = Math.min(r, 1 / 30); s.step(step, speed); r -= step }
  }

  private avoidAll(snake: Snake, others: Snake[]): void {
    if (!snake.alive) return; let ax = 0, az = 0; let urgent = false
    for (const o of others) { if (o === snake || !o.alive || !o.visible) continue
      for (let i = 0; i < o.segments.length; i++) { const seg = o.segments[i]!
        const dx = snake.headX - seg.x, dz = snake.headZ - seg.z, dist = Math.sqrt(dx * dx + dz * dz)
        if (dist < AVOIDANCE_DIST && dist > 1) {
          const w = ((1 - dist / AVOIDANCE_DIST) ** 3) * 5 // cubic falloff, very strong
          ax += (dx / dist) * w; az += (dz / dist) * w
          if (dist < 200) urgent = true
        }
      }
    }
    if (ax * ax + az * az > 0.01) {
      const blend = urgent ? 0.95 : 0.8 // emergency: almost full override
      snake.desiredBearing = normalizeRadians(snake.desiredBearing * (1 - blend) + Math.atan2(az, ax) * blend)
    }
  }

  private simulate(t: number, dt: number): void {
    // Scene 1 (0–12): 0-3 text1, 3-4 text2, 4-7 bots enter, 7-8 text3, 8-11 players+bots flee
    if (t < 12) {
      const botsVisible = t >= 0.5 // earliest bot starts at 0.5
      const playersSpawned = t >= 8
      const allScene1Snakes = [...this.s1Bots, ...this.s1Players]

      // Bots enter staggered — each has own start time
      if (botsVisible) {
        for (let i = 0; i < this.s1Bots.length; i++) { const b = this.s1Bots[i]!
          const botStart = this.s1BotStartTimes[i] ?? 1
          if (t < botStart) continue // not yet
          if (!b.visible) b.visible = true
          if (playersSpawned) {
            // Flee: curve away from center
            const awayAngle = Math.atan2(b.headZ, b.headX)
            b.desiredBearing = awayAngle + Math.sin(t * 1.5 + i * 0.8) * 0.2
          } else {
            // Head toward own target with S-curve
            const tgt = this.s1BotTargets[i] ?? { x: 0, z: 0 }
            const dx = tgt.x - b.headX, dz = tgt.z - b.headZ
            const dist = Math.sqrt(dx * dx + dz * dz)
            if (dist > 400) {
              b.desiredBearing = Math.atan2(dz, dx) + Math.sin(t * 1.8 + i * 1.3) * 0.25
            } else {
              // Orbit near target — gentle curves, not tight circles
              b.desiredBearing += (0.012 + Math.sin(t * 0.7 + i * 0.9) * 0.008)
            }
          }
          this.avoidAll(b, allScene1Snakes); this.ss(b, dt)
        }
      }

      // 4 players spawn staggered at t=8, 8.3, 8.5, 8.8
      const playerSpawnTimes = [8, 8.3, 8.5, 8.8]
      const playerTargets = [
        { x: -400, z: -300 }, { x: 500, z: -200 },
        { x: -300, z: 200 }, { x: 400, z: 300 },
      ]
      for (let pi = 0; pi < this.s1Players.length; pi++) {
        const p = this.s1Players[pi]!
        const spawnT = playerSpawnTimes[pi]!
        if (t >= spawnT && !p.visible) p.visible = true
        if (p.visible) {
          // Top-left player (index 2): veer left at the very end
          if (pi === 2 && t > 10.5) {
            p.desiredBearing = Math.PI // hard left
          } else {
            const tgt = playerTargets[pi]!
            const dx = tgt.x - p.headX, dz = tgt.z - p.headZ
            const dist = Math.sqrt(dx * dx + dz * dz)
            if (dist > 400) {
              p.desiredBearing = Math.atan2(dz, dx) + Math.sin(t * (1.3 + pi * 0.2)) * 0.15
            } else {
              p.desiredBearing += 0.013 + Math.sin(t * (0.7 + pi * 0.15)) * 0.009
            }
          }
          this.avoidAll(p, allScene1Snakes)
          if (Math.abs(p.headX) > 1500 || Math.abs(p.headZ) > 1500) {
            p.desiredBearing = Math.atan2(-p.headZ, -p.headX)
          }
          this.ss(p, dt)
        }
      }
    }

    // Scene 2 (12.5–22):
    // 12.5–14: text "Two modes" only
    // 14+: collector appears and starts collecting
    if (t >= 12.5 && t < 22.5) {
      if (t >= 14 && !this.collector.visible) this.collector.visible = true

      if (this.collector.alive && this.collector.visible) {
        if (!this.bot1HunterMode) {
          // Collector mode
          const ns = this.collSparks.reduce<SparkDot | null>((b, sp) => { if (sp.collected) return b
            const d = (sp.x - this.collector.headX) ** 2 + (sp.z - this.collector.headZ) ** 2
            return !b || d < ((b.x - this.collector.headX) ** 2 + (b.z - this.collector.headZ) ** 2) ? sp : b }, null)
          if (ns) {
            this.collector.desiredBearing = Math.atan2(ns.z - this.collector.headZ, ns.x - this.collector.headX) + Math.sin(t * 2.5) * 0.1
          } else if (this.allSparksEatenTime === 0) {
            this.allSparksEatenTime = t
            this.collector.desiredBearing = Math.PI / 2 + Math.sin(t * 2) * 0.2
          } else if (t - this.allSparksEatenTime < 1.5) {
            // Continue as collector for 1.5s after eating
            this.collector.desiredBearing = Math.PI / 2 + Math.sin(t * 2) * 0.2
          } else {
            // 1.5s passed → show "Hunter" text
            if (this.hunterTextTime === 0) this.hunterTextTime = t
            // Sharp turn UP — this is the dramatic reversal
            this.collector.desiredBearing = -Math.PI / 2
            if (t - this.hunterTextTime >= 0.5) {
              this.bot1HunterMode = true
              this.hunter.visible = true // victim appears from top
            }
          }
          this.ss(this.collector, dt)
          for (const sp of this.collSparks) { if (!sp.collected && (this.collector.headX - sp.x) ** 2 + (this.collector.headZ - sp.z) ** 2 < 100 ** 2) { sp.collected = true; this.collector.grow() } }
        } else {
          // Hunter mode: heading UP toward victim, at close range sharp turn to cut across
          if (this.hunter.alive && !this.collDead) {
            const dx = this.hunter.headX - this.collector.headX
            const dz = this.hunter.headZ - this.collector.headZ
            const dist = Math.sqrt(dx * dx + dz * dz)

            if (dist > 800) {
              // Rush upward toward victim with burst
              const toVictim = Math.atan2(dz, dx)
              this.collector.desiredBearing = toVictim + Math.sin(t * 3) * 0.05
              this.ss(this.collector, dt, 2000)
            } else {
              // Within 800wu — sharp perpendicular turn to cut across
              const toVictim = Math.atan2(dz, dx)
              this.collector.desiredBearing = toVictim + Math.PI / 2
              this.ss(this.collector, dt, 1200)
            }

            // Collision: victim head into hunter body
            for (let i = 2; i < this.collector.segments.length; i++) { const seg = this.collector.segments[i]!
              if ((this.hunter.headX - seg.x) ** 2 + (this.hunter.headZ - seg.z) ** 2 < 80 ** 2) {
                this.collDead = true; this.hunter.alive = false; this.killTime = t
                for (const vs of this.hunter.segments) for (let j = 0; j < 3; j++) { const a = Math.random() * Math.PI * 2; const sp = 40 + Math.random() * 100
                  this.collDeathSparks.push({ x: vs.x, z: vs.z, vx: Math.cos(a) * sp, vz: Math.sin(a) * sp, alpha: 0.8 + Math.random() * 0.2 }) }; break } }
          }
          if (this.collDead) {
            // Back to collector — steer toward nearest live spark
            let nearX = 0, nearZ = 0, nearDist = Infinity, found = false
            for (const sp of this.collDeathSparks) {
              if (sp.alpha <= 0.1) continue
              const dx = sp.x - this.collector.headX, dz = sp.z - this.collector.headZ
              const d = dx * dx + dz * dz
              if (d < nearDist) { nearDist = d; nearX = sp.x; nearZ = sp.z; found = true }
            }
            if (found) {
              this.collector.desiredBearing = Math.atan2(nearZ - this.collector.headZ, nearX - this.collector.headX)
            }
            this.ss(this.collector, dt)
            for (const sp of this.collDeathSparks) {
              if (sp.alpha > 0.1 && (this.collector.headX - sp.x) ** 2 + (this.collector.headZ - sp.z) ** 2 < 120 ** 2) {
                sp.alpha = 0
              }
            }
          }
        }
      }

      // Victim (fat collector): enters from TOP, heading down with gentle wave
      if (this.hunter.alive && this.hunter.visible) {
        this.hunter.desiredBearing = Math.PI / 2 + Math.sin(t * 2) * 0.12
        this.ss(this.hunter, dt)
      }
    }
    for (const sp of this.collDeathSparks) { sp.x += sp.vx * dt; sp.z += sp.vz * dt; sp.vx *= 0.97; sp.vz *= 0.97; sp.alpha = Math.max(0, sp.alpha - dt * 0.15) }

    // Scene 3 (23.5–30): text first 1.5s, then snakes appear
    if (t >= 23.5 && t < 30) {
      const snakesVisible = t >= 25 // snakes appear 1.5s after scene start
      if (snakesVisible) {
        this.s3Player.desiredBearing = Math.sin(t * 1.3) * 0.12
        this.ss(this.s3Player, dt, 1100)
        const allS3 = [this.s3Player, ...this.s3Hunters]
        for (const h of this.s3Hunters) {
          const dx = this.s3Player.headX - h.headX, dz = this.s3Player.headZ - h.headZ
          h.desiredBearing = Math.atan2(dz, dx) + Math.sin(t * 2 + h.headX * 0.002) * 0.08
          this.avoidAll(h, allS3) // avoid other hunters AND player
          this.ss(h, dt, 950)
        }
      }
    }

    // Scene 4 (31–35): player from right, bot from left, head-on, player sharp turn up
    if (t >= 31 && t < 35) {
      // Bot heads straight right toward player
      if (this.s4Bot.alive) {
        this.s4Bot.desiredBearing = 0 + Math.sin(t * 2) * 0.05
        this.ss(this.s4Bot, dt, 1100)
      }

      // Player heads left, when bot close → sharp burst turn UP
      const dx = this.s4Bot.headX - this.s4Player.headX, dz = this.s4Bot.headZ - this.s4Player.headZ
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist > 1200) {
        this.s4Player.desiredBearing = Math.PI + Math.sin(t * 1.5) * 0.08 // heading left
        this.ss(this.s4Player, dt, 1100)
      } else {
        // Sharp turn upward with burst speed
        this.s4Player.desiredBearing = -Math.PI / 2
        this.ss(this.s4Player, dt, 1800)
      }

      // Collision: bot head into player body
      if (this.s4Bot.alive && !this.s4Dead) {
        for (let i = 3; i < this.s4Player.segments.length; i++) { const seg = this.s4Player.segments[i]!
          if ((this.s4Bot.headX - seg.x) ** 2 + (this.s4Bot.headZ - seg.z) ** 2 < 80 ** 2) {
            this.s4Dead = true; this.s4Bot.alive = false
            for (const vs of this.s4Bot.segments) for (let j = 0; j < 3; j++) { const a = Math.random() * Math.PI * 2; const sp = 40 + Math.random() * 100
              this.s4Sparks.push({ x: vs.x, z: vs.z, vx: Math.cos(a) * sp, vz: Math.sin(a) * sp, alpha: 0.8 + Math.random() * 0.2 }) }; break } }
      }
    }
    for (const sp of this.s4Sparks) { sp.x += sp.vx * dt; sp.z += sp.vz * dt; sp.vx *= 0.97; sp.vz *= 0.97; sp.alpha = Math.max(0, sp.alpha - dt * 0.15) }

    // Camera: static at (0,0) for scene 1, follow bot1 only when visible in scene 2
    // Camera: static for scene 1, pre-move to collector start pos during scene 2 gap,
    // then follow collector smoothly
    if (t >= 12.5 && t < 22.5) {
      // Start moving toward collector position even before bot appears
      this.camX += (this.collector.headX - this.camX) * 0.03
      this.camZ += (this.collector.headZ - this.camZ) * 0.03
    } else {
      this.camX += (0 - this.camX) * 0.08
      this.camZ += (0 - this.camZ) * 0.08
    }
  }

  private sceneFade(t: number, start: number, end: number, fd = 0.5): number {
    if (t < start || t > end) return 0; return Math.min(Math.min(1, (t - start) / fd), Math.min(1, (end - t) / fd))
  }

  private toSX(wx: number): number { return (wx - this.camX) * WORLD_SCALE * SCALE + (this.canvasW * SCALE) / 2 }
  private toSZ(wz: number): number { return (wz - this.camZ) * WORLD_SCALE * SCALE + (this.canvasH * SCALE) * 0.45 }

  private draw(t: number): void {
    const ctx = this.ctx!; const w = this.canvasW * SCALE; const h = this.canvasH * SCALE; const s = SCALE
    const gfO = t >= 42 && t < 44; const gfI = t >= 44 // double-length fade at end
    const gf = gfO ? Math.max(0, 1 - (t - 26) / 2) : gfI ? Math.min(1, (t - 28) / 2) : 1

    ctx.clearRect(0, 0, w, h); ctx.fillStyle = NeonColors.bgDark; ctx.fillRect(0, 0, w, h)

    // Grid
    const gs = 300; ctx.strokeStyle = 'rgba(255,255,255,0.025)'; ctx.lineWidth = 1
    const gsX = Math.floor((this.camX - this.canvasW / WORLD_SCALE / 2) / gs) * gs
    const gsZ = Math.floor((this.camZ - this.canvasH / WORLD_SCALE / 2) / gs) * gs
    for (let gx = gsX; gx < this.camX + this.canvasW / WORLD_SCALE; gx += gs) { const sx = this.toSX(gx); ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, h); ctx.stroke() }
    for (let gz = gsZ; gz < this.camZ + this.canvasH / WORLD_SCALE; gz += gs) { const sz = this.toSZ(gz); ctx.beginPath(); ctx.moveTo(0, sz); ctx.lineTo(w, sz); ctx.stroke() }

    // Edge fades
    const fs = 30 * s
    let gr = ctx.createLinearGradient(0, 0, 0, fs); gr.addColorStop(0, NeonColors.bgDark); gr.addColorStop(1, 'rgba(0,2,10,0)'); ctx.fillStyle = gr; ctx.fillRect(0, 0, w, fs)
    gr = ctx.createLinearGradient(0, h - fs, 0, h); gr.addColorStop(0, 'rgba(0,2,10,0)'); gr.addColorStop(1, NeonColors.bgDark); ctx.fillStyle = gr; ctx.fillRect(0, h - fs, w, fs)
    gr = ctx.createLinearGradient(0, 0, fs, 0); gr.addColorStop(0, NeonColors.bgDark); gr.addColorStop(1, 'rgba(0,2,10,0)'); ctx.fillStyle = gr; ctx.fillRect(0, 0, fs, h)
    gr = ctx.createLinearGradient(w - fs, 0, w, 0); gr.addColorStop(0, 'rgba(0,2,10,0)'); gr.addColorStop(1, NeonColors.bgDark); ctx.fillStyle = gr; ctx.fillRect(w - fs, 0, fs, h)

    // Title
    ctx.textAlign = 'center'; ctx.textBaseline = 'top'
    ctx.font = `bold ${TITLE_FONT_SIZE}px ${FONTS.title}`; ctx.fillStyle = NeonColors.primary
    ctx.shadowColor = NeonColors.primaryGlow; ctx.shadowBlur = 12 * s; ctx.globalAlpha = 0.95 * gf
    ctx.fillText('BOTS', w / 2, 53 * s); ctx.shadowBlur = 0; ctx.globalAlpha = 1

    const textCZ = 80 * s // text area below title

    const drawFieldSparks = (sparks: SparkDot[], a: number) => { for (const sp of sparks) { if (sp.collected) continue
      const sx = this.toSX(sp.x), sz = this.toSZ(sp.z); const p = 0.6 + Math.sin(performance.now() * 0.004 + sp.x * 0.01) * 0.3
      ctx.beginPath(); ctx.arc(sx, sz, 2 * s, 0, Math.PI * 2); ctx.fillStyle = NeonColors.gold; ctx.globalAlpha = p * 0.8 * a
      ctx.shadowColor = NeonColors.gold; ctx.shadowBlur = 3 * s; ctx.fill(); ctx.shadowBlur = 0 }; ctx.globalAlpha = 1 }
    const drawDS = (sparks: DeathSpark[], a: number) => { for (const sp of sparks) { if (sp.alpha <= 0.01) continue
      const sx = this.toSX(sp.x), sz = this.toSZ(sp.z); const r = (1.5 + (Math.abs(sp.vx) % 1)) * s
      ctx.beginPath(); ctx.arc(sx, sz, r, 0, Math.PI * 2); ctx.fillStyle = NeonColors.gold; ctx.globalAlpha = sp.alpha * 0.8 * a
      ctx.shadowColor = NeonColors.gold; ctx.shadowBlur = r * 2; ctx.fill(); ctx.shadowBlur = 0 }; ctx.globalAlpha = 1 }

    // ---- SCENE 1 (0–12): fade out last 1s ----
    const s1 = this.sceneFade(t, 0, 12, 1.0)
    if (s1 > 0) {
      drawFieldSparks(this.s1Sparks, s1 * gf)
      for (let i = 0; i < this.s1Bots.length; i++) { const isDark = i === 1 || i === 4 || i === 6 // mixed dark/white
        this.drawSnk(ctx, this.s1Bots[i]!, s, s1 * gf, isDark ? '#606060' : '#E8E0D8', isDark ? '#505050' : '#E0D8D0', isDark ? '#707070' : '#F0E8E0', isDark ? '#CC5555' : '#88BBEE') }

      // 4 players with game colors, staggered spawn, 3 sec blink
      const pSpawnTimes = [8, 8.3, 8.5, 8.8]
      for (let pi = 0; pi < this.s1Players.length; pi++) {
        const p = this.s1Players[pi]!
        if (!p.visible) continue
        const spawnT = pSpawnTimes[pi]!
        const elapsed = t - spawnT
        const blink = elapsed < 3 ? (Math.sin(elapsed * (9 + pi)) > 0 ? 0.9 : 0.12) : 1
        const col = this.s1PlayerColors[pi]!
        this.drawSnk(ctx, p, s, s1 * gf * blink, col.b1, col.b2, col.h, col.g)
        if (elapsed >= 0 && elapsed < 0.5) { const fp = elapsed / 0.5
          const sx = this.toSX(p.headX), sz = this.toSZ(p.headZ)
          ctx.beginPath(); ctx.arc(sx, sz, 30 * s * (1 - fp), 0, Math.PI * 2)
          ctx.fillStyle = '#FFFFFF'; ctx.globalAlpha = (1 - fp) * 0.5 * s1 * gf; ctx.fill() }
      }

      // Text — one line at a time, exact timing per spec
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.textBright
      const textY = h * 0.5
      if (t < 3) {
        // 0–3: "The map always has 30 snakes" (empty field)
        const a = Math.min(1, t / 0.4) * (t > 2.5 ? Math.max(0, 1 - (t - 2.5) / 0.5) : 1)
        ctx.globalAlpha = a * s1 * 0.9 * gf
        ctx.fillText('The map always has 30 snakes', w / 2, textY)
      } else if (t < 7) {
        // 3–7: "Bots fill empty slots to keep it fun" (bots enter at t=4)
        const a = Math.min(1, (t - 3) / 0.4) * (t > 6.5 ? Math.max(0, 1 - (t - 6.5) / 0.5) : 1)
        ctx.globalAlpha = a * s1 * 0.9 * gf
        ctx.fillText('Bots fill empty slots to keep it fun', w / 2, textY)
      } else {
        // 7+: "Players in, bots out" (players enter at t=8)
        const a = Math.min(1, (t - 7) / 0.4)
        ctx.globalAlpha = a * s1 * 0.9 * gf
        ctx.fillText('Players in — bots out', w / 2, textY)
      }
    }

    // ---- SCENE 2: starts at t=12.5, ends 0.5s later at 22.5 ----
    const s2 = this.sceneFade(t, 12.5, 22.5, 0.8)
    if (s2 > 0) {
      drawFieldSparks(this.collSparks, s2 * gf)
      // Bot1: white→dark when becoming hunter, dark→white after kill
      if (this.collector.alive) {
        let huntP = 0
        if (this.hunterTextTime > 0) {
          huntP = Math.min(1, Math.max(0, (t - this.hunterTextTime - 0.5) / 1.5))
        }
        // After kill: transition back to white over 1.5s
        if (this.killTime > 0) {
          const backP = Math.min(1, (t - this.killTime) / 1.5)
          huntP = huntP * (1 - backP) // fade hunter color back to 0
        }
        this.drawSnk(ctx, this.collector, s, s2 * gf,
          this.lerp('#E8E0D8', '#505050', huntP), this.lerp('#E0D8D0', '#404040', huntP),
          this.lerp('#F0E8E0', '#606060', huntP), this.lerp('#88BBEE', '#CC4444', huntP))
      }
      // Victim: fat white collector from bottom
      if (this.hunter.visible && this.hunter.alive) {
        this.drawSnk(ctx, this.hunter, s, s2 * gf, '#E8E0D8', '#E0D8D0', '#F0E8E0', '#88BBEE')
      }
      drawDS(this.collDeathSparks, s2 * gf)

      // Text — sequential, at 67% from top (33% from bottom)
      const s2TextZ = h * 0.67
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.font = HEADING_FONT

      if (this.hunterTextTime > 0) {
        // "and [Hunter] mode" — Hunter in red, rest white
        const a = Math.min(1, (t - this.hunterTextTime) / 0.4)
        ctx.globalAlpha = a * s2 * 0.9 * gf
        const prefix = 'and '
        const word = 'Hunter'
        const suffix = ' mode'
        ctx.font = HEADING_FONT
        const pw = ctx.measureText(prefix).width
        const ww = ctx.measureText(word).width
        const sw = ctx.measureText(suffix).width
        const totalW = pw + ww + sw
        const startX = w / 2 - totalW / 2
        ctx.textAlign = 'left'
        ctx.fillStyle = NeonColors.textBright; ctx.fillText(prefix, startX, s2TextZ)
        ctx.fillStyle = NeonColors.danger; ctx.fillText(word, startX + pw, s2TextZ)
        ctx.fillStyle = NeonColors.textBright; ctx.fillText(suffix, startX + pw + ww, s2TextZ)
        ctx.textAlign = 'center'
      } else if (t >= 14 && this.collector.visible) {
        // "Collecting mode"
        const a = Math.min(1, (t - 14) / 0.4)
        ctx.fillStyle = NeonColors.textBright
        ctx.globalAlpha = a * s2 * 0.9 * gf
        ctx.fillText('Collecting mode', w / 2, s2TextZ)
      } else {
        // "Bots have two modes"
        ctx.fillStyle = NeonColors.textBright
        ctx.globalAlpha = s2 * 0.8 * gf
        ctx.fillText('Bots have two modes', w / 2, s2TextZ)
      }
    }

    // ---- SCENE 3 (proper fade gap from scene 2) ----
    const s3 = this.sceneFade(t, 23.5, 30, 1.0)
    if (s3 > 0) {
      // Snakes only visible after 1.5s of text
      if (t >= 25) {
        this.drawSnk(ctx, this.s3Player, s, s3 * gf, this.pB1, this.pB2, this.pH, this.pG)
        for (const h of this.s3Hunters) this.drawSnk(ctx, h, s, s3 * gf, '#606060', '#505050', '#707070', '#CC5555')
      }
      // Text: two phases
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.textBright
      if (t < 26.5) {
        // First text centered on screen
        const a = Math.min(1, (t - 23.5) / 0.4)
        const fadeA = t > 26 ? Math.max(0, 1 - (t - 26) / 0.5) : 1
        ctx.globalAlpha = a * fadeA * s3 * 0.9 * gf
        ctx.fillText('The more sparks you carry...', w / 2, h * 0.5)
      } else {
        // Second text at 67% from top (33% from bottom)
        const a = Math.min(1, (t - 26.5) / 0.4)
        ctx.globalAlpha = a * s3 * 0.9 * gf
        ctx.fillText('...the more attention you attract', w / 2, h * 0.67)
      }
    }

    // ---- SCENE 4 ----
    const s4 = this.sceneFade(t, 31, 35, 1.0)
    if (s4 > 0) {
      this.drawSnk(ctx, this.s4Player, s, s4 * gf, this.pB1, this.pB2, this.pH, this.pG)
      if (this.s4Bot.alive) this.drawSnk(ctx, this.s4Bot, s, s4 * gf, '#606060', '#505050', '#707070', '#CC5555')
      drawDS(this.s4Sparks, s4 * gf)
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.textBright
      ctx.globalAlpha = s4 * 0.9 * gf; ctx.fillText('Bots follow the same rules', w / 2, h * 0.67 - 8 * s)
      ctx.globalAlpha = s4 * 0.8 * gf; ctx.fillText('and yes, you can kill them', w / 2, h * 0.67 + 8 * s)
    }

    // ---- SCENE 5: two sequential texts, center screen ----
    const s5 = this.sceneFade(t, 36, 42, 1.0)
    if (s5 > 0) {
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.textBright
      if (t < 39) {
        // "Every bot pays 1⭐ to enter"
        const a = Math.min(1, (t - 36) / 0.4) * (t > 38.5 ? Math.max(0, 1 - (t - 38.5) / 0.5) : 1)
        ctx.globalAlpha = a * s5 * 0.9 * gf
        ctx.fillText('Every bot pays 1 ⭐ to enter', w / 2, h * 0.5)
      } else {
        // "Go collect them all!"
        const a = Math.min(1, (t - 39) / 0.4)
        ctx.globalAlpha = a * s5 * 0.9 * gf
        ctx.fillText('Go collect them all!', w / 2, h * 0.5)
      }
    }

    ctx.globalAlpha = 1
    if (gf < 1) { ctx.fillStyle = NeonColors.bgDark; ctx.globalAlpha = 1 - gf; ctx.fillRect(0, 0, w, h); ctx.globalAlpha = 1 }
  }

  private drawSnk(ctx: CanvasRenderingContext2D, snake: Snake, s: number, alpha: number,
    b1: string, b2: string, hd: string, gl: string): void {
    const segs = snake.segments; const count = segs.length
    if (count < 1 || !snake.visible || alpha < 0.01) return
    const bodyR = INITIAL_WIDTH * WORLD_SCALE * s * 0.5; const headR = bodyR * 1.3
    for (let i = Math.min(count - 1, 20); i >= 0; i--) { const sx = this.toSX(segs[i]!.x), sz = this.toSZ(segs[i]!.z)
      ctx.beginPath(); ctx.arc(sx, sz, bodyR * 1.5, 0, Math.PI * 2); ctx.fillStyle = gl; ctx.globalAlpha = 0.045 * alpha; ctx.fill() }
    for (let i = count - 1; i >= 1; i--) { const sx = this.toSX(segs[i]!.x), sz = this.toSZ(segs[i]!.z)
      ctx.beginPath(); ctx.arc(sx, sz, bodyR, 0, Math.PI * 2); ctx.fillStyle = i % 2 === 0 ? b1 : b2
      ctx.globalAlpha = (i < 3 ? 0.5 + i * 0.15 : 0.94) * alpha; ctx.fill() }
    ctx.globalAlpha = 1
    const hx = this.toSX(segs[0]!.x), hz = this.toSZ(segs[0]!.z)
    ctx.beginPath(); ctx.arc(hx, hz, headR, 0, Math.PI * 2); ctx.fillStyle = hd
    ctx.shadowColor = gl; ctx.shadowBlur = headR; ctx.globalAlpha = alpha; ctx.fill(); ctx.shadowBlur = 0
    if (count >= 2) { const dx = segs[0]!.x - segs[1]!.x, dz = segs[0]!.z - segs[1]!.z
      const bearing = Math.atan2(dz, dx); const eco = Math.cos(bearing), esi = Math.sin(bearing)
      const off = headR * 0.55, r = headR * 0.28
      for (const side of [-1, 1]) { const ex = hx + eco * off * 0.4 - esi * off * side, ez = hz + esi * off * 0.4 + eco * off * side
        ctx.beginPath(); ctx.arc(ex, ez, r, 0, Math.PI * 2); ctx.fillStyle = gl; ctx.globalAlpha = alpha; ctx.fill()
        ctx.beginPath(); ctx.arc(ex + eco * r * 0.4, ez + esi * r * 0.4, r * 0.4, 0, Math.PI * 2); ctx.fillStyle = '#000000'; ctx.fill() } }
    ctx.globalAlpha = 1
  }

  private lerp(a: string, b: string, t: number): string {
    const pa = (hex: string) => { const v = parseInt(hex.slice(1), 16); return [(v >> 16) & 0xFF, (v >> 8) & 0xFF, v & 0xFF] }
    const ca = pa(a), cb = pa(b)
    const r = Math.round(ca[0]! + (cb[0]! - ca[0]!) * t)
    const g = Math.round(ca[1]! + (cb[1]! - ca[1]!) * t)
    const bl = Math.round(ca[2]! + (cb[2]! - ca[2]!) * t)
    return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0')}`
  }
}
